import { inject, Injectable } from '@angular/core';
import { TrackedMessagesApiService, Visibility } from '@vendasta/email';
import { ConversationChannel } from '@vendasta/conversation';
import { ConversationMessage } from '../interface/conversation.interface';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

/** `resolved` exists only where a content record came back, so its text passed the gate. */
export type PersonalEmailContent = { kind: 'resolved'; subject: string; body: string } | { kind: 'withheld' };

const NO_EMAIL_CONTENT: PersonalEmailContent = Object.freeze({ kind: 'resolved', subject: '', body: '' });

@Injectable({ providedIn: 'root' })
export class PersonalEmailContentService {
  private readonly trackedMessagesApi = inject(TrackedMessagesApiService);

  /**
   * One message's email content, through the per-viewer path that enforces visibility.
   *
   * Separate from enrichMessages because only here is it known whether a record came back, which is
   * the fact a heading may be shown on. enrichMessages only decorates a thread already open.
   *
   * A missing record resolves to no text, not withheld: the lookup ran and found nothing.
   *
   * The gate is the presence of `content`, not `visibility`. Email decides per viewer server-side
   * (AccessReadTrackedMessageContent, on the content's owner and allowed users) and omits the block
   * when the answer is no; visibility is a classification carried alongside, never read by that
   * check, so PRIVATE and INTERNAL arriving here are content the viewer may read. The redaction
   * check below is belt-and-braces on top of that.
   */
  contentForMessage(message: ConversationMessage, namespace: string): Observable<PersonalEmailContent> {
    if (!message?.externalMessageId) {
      return of(NO_EMAIL_CONTENT);
    }

    return this.trackedMessagesApi
      .getMultiTrackedMessage({ namespace, trackedMessageIds: [message.externalMessageId] })
      .pipe(
        map((response) => {
          const trackedMessage = (response.trackedMessages ?? []).find(
            (r) => r.trackedMessageId === message.externalMessageId,
          )?.trackedMessage;
          const content = trackedMessage?.content;

          // An absent content block is IAM saying no, no content row, or a swallowed repo error;
          // GetMultiWithDetails returns all three identically, so withheld covers them. Deliberately
          // the same call the CRM activity card makes, so both move together if email separates them.
          if (!content) {
            return trackedMessage ? { kind: 'withheld' as const } : NO_EMAIL_CONTENT;
          }
          // Redaction stamps a timestamp and does not clear the text, so a redacted record still
          // carries its subject. It is also stamped independently of visibility, hence both.
          if (content.visibility === Visibility.VISIBILITY_REDACTED || content.redacted) {
            return { kind: 'withheld' as const };
          }
          return {
            kind: 'resolved' as const,
            subject: content.subject ?? '',
            body: content.htmlContent ?? content.plainTextContent ?? '',
          };
        }),
      );
  }

  /**
   * Fills in personal email bodies through the per-viewer path that enforces email visibility.
   * A lookup failure is propagated, not swallowed: callers that only decorate should catch it, and
   * callers that report the outcome to the user need to tell a failure from a withheld body.
   */
  enrichMessages(messages: ConversationMessage[], namespace: string): Observable<ConversationMessage[]> {
    const isPersonalEmail = (msg: ConversationMessage) =>
      msg.channel === ConversationChannel.CONVERSATION_CHANNEL_PERSONAL_EMAIL;

    const messagesNeedingContent = messages.filter((msg) => isPersonalEmail(msg) && !msg.body && msg.externalMessageId);

    if (messagesNeedingContent.length === 0) {
      return of(messages);
    }

    const trackedMessageIds = messagesNeedingContent
      .map((msg) => msg.externalMessageId)
      .filter((id): id is string => !!id);

    return this.trackedMessagesApi.getMultiTrackedMessage({ namespace, trackedMessageIds }).pipe(
      map((response) => {
        const trackedMessagesById = new Map(
          (response.trackedMessages ?? []).map((r) => [r.trackedMessageId, r.trackedMessage]),
        );

        return messages.map((msg) => {
          if (!isPersonalEmail(msg) || msg.body || !msg.externalMessageId) {
            return msg;
          }

          const trackedMessage = trackedMessagesById.get(msg.externalMessageId);
          const content = trackedMessage?.content;

          if (!content) {
            return trackedMessage ? { ...msg, emailContentHidden: true } : msg;
          }

          if (content.visibility === Visibility.VISIBILITY_REDACTED) {
            return { ...msg, emailSubject: content.subject, emailContentHidden: true };
          }

          return {
            ...msg,
            emailSubject: content.subject,
            body: content.htmlContent ?? content.plainTextContent ?? '',
          };
        });
      }),
    );
  }
}
